import { Draggable } from 'gsap/Draggable'
import { useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import Card, { CardProps } from '@/components/Card/Card'
import { useGSAP } from '@/composables/useGSAP'
import { useTransComponents } from '@/composables/useTransComponents'

import SummaryPartSliderCursor from './SummaryPartSliderCursor'

export type SummaryPartSliderProps = {
  leaving?: boolean
  items?: CardProps[]
}

export default function SummaryPartSlider({ leaving, items }: SummaryPartSliderProps) {
  const { gsap, Draggable } = useGSAP()
  const { t } = useTranslation()
  const { components } = useTransComponents()

  const parentRef = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const baseLineRef = useRef<SVGSVGElement>(null)
  const progressLineRef = useRef<SVGSVGElement>(null)
  const itemRefs = useRef<HTMLDivElement[]>([])

  const [itemWidth, setItemWidth] = useState(0)
  const [progress, setProgress] = useState(0)
  const [intro, setIntro] = useState(0)
  const [isHover, setIsHover] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isMouseHasMoved, setIsMouseHasMoved] = useState(false)
  const [dragging, setDragging] = useState(false)

  // ITEM WIDTH AND PADDING
  useLayoutEffect(() => {
    const onResize = () => {
      if (itemRefs.current.length > 0) {
        const firstItem = itemRefs.current[0]
        setItemWidth(firstItem.offsetWidth)
      }
    }
    onResize()
    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [itemRefs])

  useLayoutEffect(() => {
    const isSmall = () => window.innerWidth < 768

    const scroller = scrollerRef.current

    const items = itemRefs.current.reverse()

    const ratio = isSmall() ? 0.5 : 1
    let maxScroll = 0
    let windowWidth = window.innerWidth
    let itemWidth = 0
    let tweens: gsap.core.Timeline[] = []
    let lineTimeline: gsap.core.Timeline | null = null

    const proxy = document.createElement('div')
    let listenScroll = true

    const introProxy = {
      value: 0,
    }

    const updateItems = () => {
      const x = draggable ? -draggable.x : 0

      const max = windowWidth * 2 * (isSmall() ? 2 : 1)
      const introOffset = (windowWidth + itemWidth / 2 + 64) / max
      const introProgress = introOffset * (1 - introProxy.value)

      const p = x / max
      const step = itemWidth / max
      const nbItems = items.length

      tweens.forEach((tween, i) => {
        const tweenProgress = nbItems + 0.25 + p * 1.125 - step * i - introProgress
        tween.progress(tweenProgress)
      })

      lineTimeline.progress(p * 0.25)
      setProgress(x / maxScroll)
    }

    const updateDrag = () => {
      if (!listenScroll) return
      const scroll = scroller.scrollLeft
      gsap.set(proxy, { x: -scroll })
      draggable?.update()

      updateItems()
    }
    const updateScroll = () => {
      scroller.scrollLeft = draggable ? -draggable.x : 0

      updateItems()
    }
    let draggable: Draggable = null

    let ctx: gsap.Context = null
    let previousWidth = 0
    const onResize = () => {
      if (window.innerWidth === previousWidth) return
      previousWidth = window.innerWidth
      ctx?.revert()
      ctx = gsap.context(() => {
        windowWidth = window.innerWidth
        itemWidth = items[0].offsetWidth + 64
        maxScroll = listRef.current.offsetWidth - window.innerWidth

        tweens = items.map((item) => {
          const timeline = gsap.timeline({ paused: true, repeat: -1 })
          timeline.set(item, { rotate: 0 })

          timeline.fromTo(
            item,
            {
              y: 0,
            },
            {
              y: 0,
              duration: 1,
              ease: 'summaryLineY',
            },
            0
          )

          return timeline
        })

        lineTimeline = gsap.timeline({ paused: true, repeat: -1 }).fromTo(
          [baseLineRef.current, progressLineRef.current],
          {
            x: -windowWidth / 2,
          },
          {
            x: windowWidth / 2,
            duration: 1,
            ease: 'none',
          },
          0
        )

        const bounds = {
          minX: -maxScroll,
          minY: 0,
          maxX: 0,
          maxY: 0,
        }

        setIsTouch('ontouchstart' in window)
        draggable = Draggable.create(proxy, {
          trigger: parentRef.current,
          type: 'x',
          bounds,
          edgeResistance: 1,
          inertia: true,
          onPress: () => {
            setDragging(true)
          },
          onRelease: () => {
            setDragging(false)
          },
          onDragStart: () => {
            setIsMouseHasMoved(false)
            listenScroll = false
          },
          onDrag: updateScroll,
          onThrowUpdate: updateScroll,
          onThrowComplete: () => {
            listenScroll = true
          },
        })[0]

        // SCROLLTRIGGER ANIMATION
        const scrollTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: parentRef.current,
            start: 'top center',
            end: 'bottom top',
            // scrub: true,
            // markers: true,
            toggleActions: 'play none none none',
            once: true,
          },
        })
        scrollTimeline.fromTo(
          itemRefs.current,
          {
            x: windowWidth + itemWidth / 2 + 64,
          },
          {
            x: 0,
            duration: 2,
            ease: 'quart.out',
          },
          0
        )

        const lineIntro = {
          value: 0,
        }
        scrollTimeline.fromTo(
          introProxy,
          {
            value: 0,
          },
          {
            value: 1,
            duration: 2,
            ease: 'quart.out',
            onUpdate: updateItems,
          },
          0
        )
        scrollTimeline.fromTo(
          lineIntro,
          {
            value: 0,
          },
          {
            value: 1,
            duration: 2,
            ease: 'power2.out',
            onUpdate: () => setIntro(lineIntro.value),
          },
          0
        )
      })
      updateItems()
    }

    scroller.addEventListener('scroll', updateDrag)
    window.addEventListener('resize', onResize)

    const resizeObserver = new ResizeObserver(onResize)
    resizeObserver.observe(listRef.current)

    onResize()

    return () => {
      ctx?.revert()
      scroller.removeEventListener('scroll', updateDrag)
      window.removeEventListener('resize', onResize)
      resizeObserver.disconnect()
    }
  }, [
    baseLineRef,
    progressLineRef,
    scrollerRef,
    listRef,
    parentRef,
    setProgress,
    setIntro,
    setIsMouseHasMoved,
    setIsTouch,
    gsap,
    Draggable,
  ])

  return (
    // scale-[0.45]
    <section
      ref={parentRef}
      className={`relative w-full pt-120 ${leaving ? 'transition-opacity duration-500 ease-out-quad opacity-0' : ''}`}
      style={{
        // @ts-expect-error CSS Variables
        '--item-width': `${itemWidth}px`,
        '--intro-progress': `${intro * 100}%`,
        '--scroll-progress': `${progress * 100}%`,
      }}
    >
      <h2
        className='text-heading-h2 text-center  mt-120 z-2 w-full'
        style={
          {
            '--color-accent': 'var(--color-mid-blue)',
            '--color-secondary': 'var(--color-peach)',
          } as React.CSSProperties
        }
      >
        <Trans components={components}>{t(`summary.highlight`)}</Trans>
      </h2>
      <div className='relative'>
        <div aria-hidden='true' className='SummaryPartSliderIntro absolute inset-0 pointer-events-none'>
          <svg
            ref={baseLineRef}
            className='absolute opacity-10 top-1/2 -translate-y-1/2 -left-1/2 w-[400vw] md:w-[200vw] h-auto -scale-x-200 md:-scale-x-100'
            viewBox='0 0 2880 82'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M0 81C209.946 81 277.228 -32.1838 386.904 14.6203C608.442 109.161 710.4 37.0767 837.209 14.6203C1137.32 -38.5251 1213.31 81 1440 80.9999C1649.95 80.9999 1717.23 -32.1838 1826.9 14.6203C2048.44 109.161 2150.4 37.0767 2277.21 14.6203C2577.32 -38.5251 2653.31 81 2880 80.9999'
              stroke='currentColor'
              strokeWidth='2'
            />
          </svg>
          <div className='SummaryPartSliderProgress absolute inset-0'>
            <svg
              ref={progressLineRef}
              className='absolute top-1/2 -translate-y-1/2 -left-1/2 w-[400vw] md:w-[200vw] h-auto -scale-x-200 md:-scale-x-100'
              viewBox='0 0 2880 82'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M0 81C209.946 81 277.228 -32.1838 386.904 14.6203C608.442 109.161 710.4 37.0767 837.209 14.6203C1137.32 -38.5251 1213.31 81 1440 80.9999C1649.95 80.9999 1717.23 -32.1838 1826.9 14.6203C2048.44 109.161 2150.4 37.0767 2277.21 14.6203C2577.32 -38.5251 2653.31 81 2880 80.9999'
                stroke='currentColor'
                strokeWidth='2'
              />
            </svg>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className='pt-120 pb-60 mb-60 overflow-x-auto no-scrollbar'
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          <div
            ref={listRef}
            className='inline-flex gap-16 md:gap-64 justify-center relative z-2 pl-[calc(50vw-var(--item-width)/2)] pr-[calc(50vw-var(--item-width)/2)]'
          >
            {items.map((item, i) => (
              <Card
                className={i > 0 ? 'opacity-100' : ''}
                ref={(item) => {
                  itemRefs.current[i] = item
                }}
                key={i}
                {...item}
              />
            ))}
          </div>
        </div>
      </div>

      {!isTouch && (
        <SummaryPartSliderCursor
          visible={isHover && isMouseHasMoved && !dragging}
          setIsMouseHasMoved={setIsMouseHasMoved}
        />
      )}
    </section>
  )
}
